import type { CefrLevel, Direction, GradeButton, Language, QuizKind, SrsState, StudyMode } from "@rmnlng/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { useMe } from "./auth";

// Per-card display mode chosen by the SRS reps count — flip card,
// multiple choice, or type-in. Renamed from StudyMode to avoid collision
// with the user-level StudyMode enum (OPEN | LEVEL) shared via Prisma.
export type CardMode = "flip" | "type" | "mcq";

// Quiz cards have a shorter ladder — no flip mode, since the prompt
// IS the question (nothing to "reveal" before answering).
export type QuizCardMode = "mcq" | "type";

export type WordReviewCard = {
  kind: "WORD";
  wordId: string;
  direction: Direction;
  isNew: boolean;
  mode: CardMode;
  mcqDistractors?: string[];
  prompt: {
    lemma: string;
    language: Language;
    pos: string | null;
    gender: string | null;
    pronunciation: string | null;
  };
  answer: {
    translations: Array<{ id: string; lemma: string }>;
    synonyms: Array<{ id: string; lemma: string }>;
    examples: Array<{ text: string; translation: string }>;
    notes: string | null;
    // Curator-authored image — the renderer prefers this when set.
    imageUrl: string | null;
    imageAttribution: string | null;
    // English search query for the on-demand image service
    // (`apps/images` → Pexels). The renderer builds the URL itself
    // (`${VITE_IMAGE_URL}/v1/image?q=...`) and falls back to the "no
    // image" placeholder if the service returns 404 (negative cache).
    // Server emits null when curator imageUrl is set, when the word's
    // POS isn't in the image allowlist, or when there's no gloss yet.
    imageQuery: string | null;
  };
  state: SrsState;
  preview: Record<GradeButton, number>;
};

export type GrammarReviewCard = {
  kind: "GRAMMAR";
  ruleId: string;
  isNew: boolean;
  title: string;
  body: string;
  tags: string[];
  state: SrsState;
  preview: Record<GradeButton, number>;
};

export type QuizReviewCard = {
  kind: "QUIZ";
  quizItemId: string;
  ruleId: string;
  ruleTitle: string;
  isNew: boolean;
  // Discriminator passed through from the QuizItem row. Drives the
  // renderer choice in <QuizCardView> (Phase 3a renders CLOZE; 3b
  // adds TRANSFORM).
  quizKind: QuizKind;
  // Sentence with `___` marker. UI replaces at render. For TRANSFORM,
  // this is the *target* sentence.
  sentence: string;
  translation: string;
  // TRANSFORM only — null for CLOZE.
  sourceSentence: string | null;
  sourceTranslation: string | null;
  hints: string[];
  explanation: string | null;
  // For mcq mode: 4 options total (correct + 3 distractors), pre-shuffled.
  // For type mode: omitted (UI renders an input).
  options?: string[];
  mode: QuizCardMode;
  state: SrsState;
  preview: Record<GradeButton, number>;
};

export type ReviewCard = WordReviewCard | GrammarReviewCard | QuizReviewCard;

export type ReviewSubmitInput =
  | {
      kind: "WORD";
      wordId: string;
      direction: Direction;
      grade: GradeButton;
      // Wall-clock seconds since the card was shown. Optional — omitted for
      // programmatic callers (tests, admin tooling) or if the timer is
      // unavailable. Server clamps to a sane max.
      secondsSpent?: number;
    }
  | {
      kind: "GRAMMAR";
      ruleId: string;
      grade: GradeButton;
      secondsSpent?: number;
    };

// Quiz submit shape — auto-graded server-side from `userAnswer`, so
// no `grade` field. Kept as its own input type because the response
// shape differs too (`correct` + `expectedAnswers` for the panel).
// Phase 3a wires `useSubmitQuiz` against this; Phase 2 lands the type
// for compile-time correctness on the existing card pipeline.
export type QuizSubmitInput = {
  quizItemId: string;
  userAnswer: string;
  secondsSpent?: number;
};

export type QuizSubmitResponse = {
  kind: "QUIZ";
  quizItemId: string;
  correct: boolean;
  // What the server accepted as correct — the wrong-answer panel uses
  // this to render "Correct: niște" without keeping the QuizItem cached.
  expectedAnswers: string[];
  state: SrsState;
  nextReviewAt: string;
};

// Same shape as ReviewSubmitInput minus `grade` — distinct type so the
// mutation's `variables` are self-describing at the cache-update callsite.
export type MarkKnownInput =
  | { kind: "WORD"; wordId: string; direction: Direction }
  | { kind: "GRAMMAR"; ruleId: string };

// Session flavor: WORD = vocabulary-only, GRAMMAR = rules + their quizzes.
// Absent = all kinds (used by Home for counts).
export type StudyKind = "WORD" | "GRAMMAR";

// Cache key includes everything that affects the queue's contents:
// `kind`/`includeGrammar` + the user's resolved mode/level. Different
// values get separate cache entries so flipping mode (in Settings →
// Study mode) is a clean re-fetch — no leakage from the prior mode's
// cards.
export const reviewsKeys = {
  queue: (opts: { kind?: StudyKind; includeGrammar?: boolean; mode?: StudyMode; level?: CefrLevel } = {}) =>
    ["reviews", "queue", opts] as const,
};

// The Romanian headword a word card belongs to, independent of review
// direction. A recognition card (RO→RU) IS the Romanian word, so its
// `wordId` is the identity. A production card (RU→RO) prompts with a
// Russian sense and answers with the Romanian word, so its identity is
// that answer partner. Homonyms (distinct Word rows sharing a lemma, e.g.
// `mare` sea/big) have distinct ids and stay distinct.
function headwordKey(card: WordReviewCard): string {
  if (card.direction === "RO_TO_RU") return card.wordId;
  return card.answer.translations[0]?.id ?? card.wordId;
}

/**
 * Collapse a fetched queue to at most one card per Romanian headword, so
 * the same word never surfaces twice in a single Study session — e.g.
 * `cămin` as an RO→RU recognition card AND again as an RU→RO production
 * card (or once per Russian sense). The deferred card isn't dropped; it
 * simply returns in the next session's queue.
 *
 * Applied at the query layer because the session walks a fixed snapshot
 * (`staleTime: Infinity`, graded cards filtered from cache, no mid-session
 * refetch), so deduping once here is enough. Grammar and quiz cards pass
 * through untouched.
 */
export function dedupeQueueByHeadword(cards: ReviewCard[]): ReviewCard[] {
  const seen = new Set<string>();
  const out: ReviewCard[] = [];
  for (const card of cards) {
    if (card.kind === "WORD") {
      const key = headwordKey(card);
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(card);
  }
  return out;
}

/**
 * Fetches the study queue, automatically scoped to the user's current
 * `studyMode` + `currentLevel` (read from `useMe()`). Callers don't need
 * to pass mode/level explicitly — that'd duplicate the source of truth
 * and make it easy to drift.
 *
 * In LEVEL mode the server also includes grammar implicitly (the Level
 * home shows it inline), so `includeGrammar` only matters in OPEN mode
 * where it's a per-call opt-in.
 *
 * Stays disabled until `useMe()` resolves — otherwise we'd issue an
 * unscoped query and then re-fetch the moment user data lands. With the
 * gate, the first request goes out with the right mode/level.
 */
export function useReviewQueue(opts: { kind?: StudyKind; includeGrammar?: boolean } = {}) {
  const { data: user } = useMe();
  const mode = user?.studyMode;
  const level = user?.currentLevel;

  return useQuery({
    queryKey: reviewsKeys.queue({ ...opts, mode, level }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.kind) params.set("kind", opts.kind);
      if (opts.includeGrammar) params.set("includeGrammar", "true");
      if (mode) params.set("mode", mode);
      if (level) params.set("level", level);
      const qs = params.toString();
      const cards = await apiFetch<ReviewCard[]>(`/reviews/queue${qs ? `?${qs}` : ""}`);
      return dedupeQueueByHeadword(cards);
    },
    enabled: !!user,
    staleTime: Infinity,
    refetchOnMount: "always",
  });
}

export function useSubmitReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewSubmitInput) => {
      const base =
        input.kind === "WORD"
          ? {
              targetType: "WORD" as const,
              targetId: input.wordId,
              direction: input.direction,
              grade: input.grade,
            }
          : {
              targetType: "GRAMMAR" as const,
              targetId: input.ruleId,
              grade: input.grade,
            };
      const body = input.secondsSpent != null ? { ...base, secondsSpent: input.secondsSpent } : base;
      return apiFetch<{
        kind: "WORD" | "GRAMMAR";
        state: SrsState;
        nextReviewAt: string;
      }>("/reviews", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_, variables) => {
      // Drop the reviewed card from every cached queue variant (includeGrammar on/off)
      qc.setQueriesData<ReviewCard[]>({ queryKey: ["reviews", "queue"] }, (old) =>
        old?.filter((c) => {
          if (c.kind === "WORD" && variables.kind === "WORD") {
            return !(c.wordId === variables.wordId && c.direction === variables.direction);
          }
          if (c.kind === "GRAMMAR" && variables.kind === "GRAMMAR") {
            return c.ruleId !== variables.ruleId;
          }
          return true;
        }),
      );
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["grammar"] });
      qc.invalidateQueries({ queryKey: ["words"] });
    },
  });
}

/**
 * Submit a quiz answer for auto-grading. Deliberately does NOT
 * remove the card from the queue cache on success — the renderer
 * needs to show the ✓/✗ result panel for a beat before transitioning.
 * Use `useAdvanceQuizCard` from the renderer once the user is ready
 * to move on (auto-advance after dwell on correct, manual button on
 * wrong).
 *
 * Server-side state IS updated immediately (QuizProgress + ReviewLog).
 * The deferred cache update only controls when the user sees the
 * NEXT card.
 */
export function useSubmitQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuizSubmitInput) => {
      const base = {
        targetType: "QUIZ" as const,
        targetId: input.quizItemId,
        userAnswer: input.userAnswer,
      };
      const body = input.secondsSpent != null ? { ...base, secondsSpent: input.secondsSpent } : base;
      return apiFetch<QuizSubmitResponse>("/reviews", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      // Progress (the user's mature/learning ratio shifts) — refresh
      // the by-level rings + Levels page totals. Queue cache is left
      // alone; advanceQuizCard handles that on the renderer's schedule.
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["grammar"] });
    },
  });
}

/**
 * Drops a quiz card from every cached queue variant. Called by the
 * renderer after the result panel has been shown for a beat (auto on
 * correct, on Continue click for wrong).
 */
export function useAdvanceQuizCard() {
  const qc = useQueryClient();
  return (quizItemId: string) => {
    qc.setQueriesData<ReviewCard[]>({ queryKey: ["reviews", "queue"] }, (old) =>
      old?.filter((c) => !(c.kind === "QUIZ" && c.quizItemId === quizItemId)),
    );
  };
}

export function useMarkKnown() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MarkKnownInput) => {
      const body =
        input.kind === "WORD"
          ? {
              targetType: "WORD" as const,
              targetId: input.wordId,
              direction: input.direction,
            }
          : {
              targetType: "GRAMMAR" as const,
              targetId: input.ruleId,
            };
      return apiFetch<{
        kind: "WORD" | "GRAMMAR";
        state: SrsState;
        nextReviewAt: string;
      }>("/reviews/know", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    // Mirrors `useSubmitReview` so the Study-flow cache bookkeeping stays
    // consistent whether the user grades the card or skips it via "Know".
    onSuccess: (_, variables) => {
      qc.setQueriesData<ReviewCard[]>({ queryKey: ["reviews", "queue"] }, (old) =>
        old?.filter((c) => {
          if (c.kind === "WORD" && variables.kind === "WORD") {
            return !(c.wordId === variables.wordId && c.direction === variables.direction);
          }
          if (c.kind === "GRAMMAR" && variables.kind === "GRAMMAR") {
            return c.ruleId !== variables.ruleId;
          }
          return true;
        }),
      );
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["grammar"] });
      qc.invalidateQueries({ queryKey: ["words"] });
    },
  });
}

export function formatInterval(days: number): string {
  if (days < 1) return "<1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}y`;
}
